import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiCopyToClipBoard,
  MuiGrid,
  MuiTypography,
} from "../../../../components";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
  StatusBadgeType,
} from "../../../../components/statusBadge/StatusBadge";
import { EMPTY_VALUE_DASH } from "../../../../helpers/constants";
import { getGenericAccountStatusFromExpirationDate } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { createGenericAccounts } from "../../createGenericAccount/siteCore/SiteCoreKeys";
import { ViewGenericAccountKeyDetailsWrapper } from "./ViewGenericAccountKeyDetails.style";

type ViewGenericAccountKeyDetailsType = {
  genericRegistrationCode?: string;
  accountsAssigned?: number;
  accountsRegistered?: number;
  status?: StatusBadgeType;
  expirationDate?: string;
};

const ViewGenericAccountKeyDetails = ({
  genericRegistrationCode,
  accountsAssigned,
  accountsRegistered,
  status = STATUS_BADGE_TYPE.active,
  expirationDate,
}: ViewGenericAccountKeyDetailsType) => {
  const { fetchComponentTitle, fetchLabel, fetchValueByKeyOfDropdown } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { commonLabels } = useSiteCore();

  const { createGenericAccountKeyDetailsTitle } = createGenericAccounts;
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const getLabel = (key: keyof typeof createGenericAccounts) => {
    return fetchLabel(
      createGenericAccountKeyDetailsTitle,
      createGenericAccounts[key],
    );
  };

  return (
    <ViewGenericAccountKeyDetailsWrapper>
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(
              createGenericAccounts.createGenericAccountKeyDetailsTitle,
            )}
          />
        }
        showExpandCollapseIcon={false}
      >
        <MuiGrid container className="semi-header-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiTypography variant="h3" className="header-label">
              {getLabel("genericRegistrationCode")}
            </MuiTypography>
            {genericRegistrationCode ? (
              <MuiCopyToClipBoard
                value={genericRegistrationCode}
                isValueWithEllipsis={true}
                valueClass="copy-registration-code"
                ariaLabelCopyToClipboard={commonARIALabel.copyToClipboard}
                toastText={commonLabels.copied}
                showCheckOnCopy
                showInlineToast
              />
            ) : (
              <MuiTypography variant="h4Bold">{EMPTY_VALUE_DASH}</MuiTypography>
            )}
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiTypography variant="h3" className="header-label">
              {getLabel("numberOfAccounts")}
            </MuiTypography>
            <MuiBox className="number-of-accounts">
              <MuiTypography>
                {getLabel("assigned")} :{" "}
                <MuiTypography variant="h4Bold">
                  {accountsAssigned ?? 0}
                </MuiTypography>
              </MuiTypography>
              <MuiTypography>|</MuiTypography>
              <MuiTypography>
                {getLabel("registered")} :{" "}
                <MuiTypography variant="h4Bold">
                  {accountsRegistered ?? 0}
                </MuiTypography>
              </MuiTypography>
            </MuiBox>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiTypography variant="h3" className="header-label">
              {getLabel("editViewGenericAccountStatusLabel")}
            </MuiTypography>
            <StatusBadge
              label={fetchValueByKeyOfDropdown(
                createGenericAccounts.createGenericAccountKeyDetailsTitle,
                createGenericAccounts.editViewGenericAccountStatusList,
                getGenericAccountStatusFromExpirationDate(
                  status,
                  expirationDate,
                ),
              )}
              status={getGenericAccountStatusFromExpirationDate(
                status,
                expirationDate,
              )}
            />
          </MuiGrid>
        </MuiGrid>
      </AccordionHeader>
    </ViewGenericAccountKeyDetailsWrapper>
  );
};

export default ViewGenericAccountKeyDetails;
